import { yupResolver } from '@hookform/resolvers/yup'
import { LineEdit } from '@qr-front/icons/line-edit'
import { useAtomValue } from 'jotai'
import { atomWithReset, RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { forwardRef, useImperativeHandle } from 'react'
import { Resolver, useForm } from 'react-hook-form'
import { validateContentForm } from 'utils/formUtils'
import { maxLength } from 'utils/inputChangeUtils'
import * as yup from 'yup'

import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'

import { GENERATOR_FORM_ANIMATION_DURATION } from '../components/GeneratorFormSection'
import { RhcField } from '../components/RhcField'
import { qrTypeAtom } from '../generatorState'
import { QR_TYPE } from '../generatorTypes'
import { TContentFormHandle, TQrContentFormSection } from './qrContentFormTypes'
import { useSubmitStepOnEnter } from './useSubmitStepOnEnter'

export const QR_NAME_FORM_ID = 'qr-name'
export const QR_NAME_MAX_LENGTH = 50

export const qrNameAtom = atomWithReset<string | undefined>(undefined)

export const getQrNameSchema = (t: TTranslationFunction) =>
	yup.object({
		qrName: yup.string().max(
			QR_NAME_MAX_LENGTH,
			t('ui__field_validation_errors__generic__max_length', {
				length: QR_NAME_MAX_LENGTH,
			}),
		),
	})

export type TQrNameFormField = yup.InferType<ReturnType<typeof getQrNameSchema>>

export const resetQrNameForm = () => {
	getStore().set(qrNameAtom, RESET)
}

export const QrNameForm = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()
	const qrType = useAtomValue(qrTypeAtom)
	const submitStepOnEnter = useSubmitStepOnEnter()

	const form = useForm<TQrNameFormField>({
		defaultValues: {
			qrName: getStore().get(qrNameAtom),
		},
		mode: 'onTouched',
		reValidateMode: 'onChange',
		resolver: yupResolver(
			getQrNameSchema(t),
		) as unknown as Resolver<TQrNameFormField>,
		shouldUnregister: false,
	})

	const { handleSubmit } = form

	useImperativeHandle(
		ref,
		() => ({
			validate: () =>
				validateContentForm(
					(onValid, onInvalid) =>
						handleSubmit(onValid, async (errors) => {
							onInvalid(errors)
						}),
					GENERATOR_FORM_ANIMATION_DURATION * 1.25,
				),
		}),
		[handleSubmit],
	)

	return (
		<RhcField
			type="text"
			form={form}
			path="qrName"
			fieldName="qr_name"
			atom={qrNameAtom}
			label={t('generator__content_form_section__qr_name__field_label')}
			labelClassName="whitespace-nowrap inline-block w-max shrink-0"
			placeholder={t(
				'generator__content_form_section__qr_name__field_placeholder',
			)}
			tooltip={t(
				'generator__content_form_section__qr_name__field_tooltip',
			)}
			className="col-span-2 tablet:col-span-1"
			onChangeTransform={(event) => maxLength(QR_NAME_MAX_LENGTH)(event)}
			{...(qrType === QR_TYPE.Url ? submitStepOnEnter : {})}
		/>
	)
})

QrNameForm.displayName = 'QrNameForm'

export const qrNameFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: QR_NAME_FORM_ID,
	title: t('generator__content_form_section__qr_name__title__changed'),
	// TODO: Temporary key. After deleting the old `...__title` from Lokalise, rename this __changed key back to `...__title`.
	description: t(
		'generator__content_form_section__qr_name__description__changed',
	),
	// TODO: Temporary key. After deleting the old `...__description` from Lokalise, rename this __changed key back to `...__description`.
	icon: <LineEdit />,
	Content: QrNameForm,
})
